import * as React from 'react';
import { toast } from 'sonner';

import {
  prepareImageFiles,
  readBlobAsDataUrl,
  UNSUPPORTED_IMAGE_FORMAT_MESSAGE,
} from '@/lib/prepare-image-file';

type ImageFileInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  onPrepared?: (file: File) => void | Promise<void>;
  onPreparedFiles?: (files: File[]) => void | Promise<void>;
  onPreparedDataUrl?: (dataUrl: string) => void | Promise<void>;
  onPreparingChange?: (preparing: boolean) => void;
};

export const ImageFileInput = React.forwardRef<HTMLInputElement, ImageFileInputProps>(
  function ImageFileInput(
    {
      accept = 'image/*',
      className,
      onPrepared,
      onPreparedFiles,
      onPreparedDataUrl,
      onPreparingChange,
      ...props
    },
    ref
  ) {
    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = Array.from(event.target.files ?? []);
      event.target.value = '';
      if (raw.length === 0) return;

      onPreparingChange?.(true);
      try {
        const files = await prepareImageFiles(raw);
        if (onPreparedFiles) await onPreparedFiles(files);
        const first = files[0];
        if (first && onPrepared) await onPrepared(first);
        if (first && onPreparedDataUrl) await onPreparedDataUrl(await readBlobAsDataUrl(first));
      } catch (error) {
        console.error(error);
        toast.error(error instanceof Error ? error.message : UNSUPPORTED_IMAGE_FORMAT_MESSAGE);
      } finally {
        onPreparingChange?.(false);
      }
    };

    return (
      <input
        {...props}
        ref={ref}
        type="file"
        accept={accept}
        className={className}
        onChange={handleChange}
      />
    );
  }
);

ImageFileInput.displayName = 'ImageFileInput';
